/**
 * BulkAssignOwnerModal — one person becomes the owner of every selected
 * item: the same transfer the item page makes under "Transfer ownership",
 * through itemsStore.transferOwnership, which clears any delegation and
 * reports whether the row saved.
 */
import { useMemo, useState } from 'react';
import { Modal, TextInput, Radio, Callout, EmptyState } from '@/components/ds';
import { Button } from '@/components/ui/Button';
import { useSystemUsers } from '../../../hooks/useSystemUsers';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { itemsWord, runBulk, type BulkActionResult } from './index';

export interface BulkAssignOwnerModalProps {
  open: boolean;
  selectedIds: string[];
  onClose: () => void;
  onComplete: (result: BulkActionResult) => void;
}

export function BulkAssignOwnerModal({ open, selectedIds, onClose, onComplete }: BulkAssignOwnerModalProps) {
  const [query, setQuery] = useState('');
  const [userId, setUserId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);

  const { users, loading, error } = useSystemUsers();
  const items = useItemsStore((s) => s.items);
  const transferOwnership = useItemsStore((s) => s.transferOwnership);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q
      ? users.filter(
          (u) =>
            u.name.toLowerCase().includes(q) ||
            u.email.toLowerCase().includes(q) ||
            u.metadata?.title?.toLowerCase().includes(q),
        )
      : users;
    return [...list].sort((a, b) => a.name.localeCompare(b.name));
  }, [users, query]);

  const chosen = users.find((u) => u.id === userId) ?? null;
  const count = selectedIds.length;

  const apply = async () => {
    if (!chosen || busy) return;
    setBusy(true);
    try {
      const result = await runBulk(
        'Owner assigned',
        selectedIds,
        (id) => items.find((i) => i.id === id)?.title ?? id,
        (id) => transferOwnership(id, chosen.id),
        (done, total) => setProgress({ done, total }),
      );
      onComplete(result);
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => { if (!busy) onClose(); }}
      dismissible={!busy}
      size="md"
      title={`Assign an owner to ${itemsWord(count)}`}
      description="The owner is accountable for the item. Each item's current owner is replaced, and any delegation ends."
      footer={
        <>
          <Button type="button" variant="secondary" size="sm" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="button" variant="cta" size="sm" onClick={() => void apply()} disabled={busy || !chosen}>
            {busy && progress ? `Assigning… ${progress.done} of ${progress.total}` : `Assign to ${itemsWord(count)}`}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <TextInput
          size="sm"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name, email or title"
          autoFocus
          disabled={busy}
        />
        <div className="max-h-64 overflow-y-auto border" style={{ borderColor: 'var(--rule)' }}>
          {loading && <p className="px-3 py-4 text-sm text-[var(--text-ter)]">Loading people…</p>}
          {error && <Callout tone="coral">Could not load people: {error}</Callout>}
          {!loading && !error && shown.length === 0 && (
            <EmptyState title="No one matches" description="Try another name or email." />
          )}
          {shown.map((u) => (
            <div key={u.id} className="border-b px-3 py-2 last:border-b-0" style={{ borderColor: 'var(--rule)' }}>
              <Radio
                name="bulk-owner"
                checked={u.id === userId}
                onChange={() => setUserId(u.id)}
                disabled={busy}
                label={u.name}
                description={[u.metadata?.title, u.email].filter(Boolean).join(' · ')}
              />
            </div>
          ))}
        </div>
        {chosen && (
          <Callout tone="slate">
            You are about to make <strong className="text-[var(--text-pri)]">{chosen.name}</strong> the owner of{' '}
            {itemsWord(count)}.
          </Callout>
        )}
      </div>
    </Modal>
  );
}
